import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type { TypedFormFieldConfiguration } from '@risksmart-app/form-configuration/src/field-service';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import {
  Form_Configuration_Update_Column,
  UpdateFormConfigurationDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useMemo } from 'react';
import type {
  FormEditorInitialData,
  FormEditorOutput,
} from 'src/blocks/form-editor-dialog';

import { evictField } from '@/utils/graphqlUtils';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';

import type { FormConfigurationUpdatePayload } from './buildFormConfigurationUpdatePayload';
import { buildFormConfigurationUpdatePayload } from './buildFormConfigurationUpdatePayload';
import { buildStandardFieldCanonicalMap } from './conditionPropertyKeys';

// The mutation document lives in
// `packages/web-graphql-client/graphql/formConfiguration/updateFormConfiguration.graphql`
// — a single Hasura transaction that atomically persists the entire form
// editor output (custom attribute schema, form configuration, field
// configurations, sections, ordering, deletions) so a mid-flight failure
// can never leave the DB in a partial state.

interface UseFormEditorUpdateGraphQLOptions {
  parentType: FormId;
  initialData: FormEditorInitialData;
  customAttributeSchema: FormConfigurationUpdatePayload['CustomAttributeSchema'];
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
}

/**
 * Apollo/Hasura twin of the bulk save. Builds the normalised payload via the
 * shared helper, then projects it into the Hasura-specific variable shape
 * (nested `formConfigurationObject` with `fields_ordering.data`,
 * `sectionConfig`, `sectionOrdering`, `fieldMappings`,
 * `customAttributeSchemas`, plus the `fieldIds`/`sectionIds`/
 * `customFieldsToDelete` delete predicates) the existing single atomic
 * mutation expects.
 */
export const useFormEditorUpdateGraphQL = ({
  parentType,
  initialData,
  customAttributeSchema,
  formFieldConfigurations,
}: UseFormEditorUpdateGraphQLOptions): ((
  output: FormEditorOutput
) => Promise<void>) => {
  const evictFormCaches = (cache: ApolloCache<unknown>) => {
    evictField(cache, 'form_configuration');
    evictField(cache, 'form_field_configuration');
    evictField(cache, 'form_field_ordering');
    evictField(cache, 'form_section_configuration');
    evictField(cache, 'form_section_field');
    evictField(cache, 'form_section_ordering');
    evictField(cache, 'custom_attribute_schema');
  };

  const [updateFormConfiguration] = useMutation(
    UpdateFormConfigurationDocument,
    {
      update: evictFormCaches,
      // The form-config reads (`getFormCustomisation` backs the live page form,
      // `getFormConfigurationByParentType` backs the field modal) run through
      // `createQueryHook`, whose default Apollo fetchPolicy is `no-cache`. A
      // no-cache query never reads the normalised cache, so `evictFormCaches`
      // alone can't make it re-run — the new field would only appear after a
      // full page reload (CUS-108 Bug A). Force a network refetch of both reads
      // and await it so the dialog can't reopen against a stale baseline and
      // mint an orphan attribute on the next save (CUS-108 Bug B).
      refetchQueries: [
        'getFormCustomisation',
        'getFormConfigurationByParentType',
      ],
      awaitRefetchQueries: true,
    }
  );

  // Lookup: lowercase React key → canonical FieldId. Conditions are
  // evaluated against form values, which use the canonical casing.
  const formConfig = useFormConfigRegistry()[parentType];
  const standardFieldCanonicalById = useMemo(
    () => buildStandardFieldCanonicalMap(formConfig),
    [formConfig]
  );

  return useCallback(
    async (output: FormEditorOutput) => {
      const payload = buildFormConfigurationUpdatePayload({
        output,
        parentType,
        initialData,
        customAttributeSchema,
        formFieldConfigurations,
        standardFieldCanonicalById,
      });

      // ---- Project the normalised payload into Hasura variables ----
      // form_field_configuration upsert rows: shared payload + the
      // FormConfigurationParentType the Hasura row needs explicitly.
      const fieldConfigurations = payload.FieldConfigurations.map((fc) => ({
        FormConfigurationParentType: parentType,
        FieldId: fc.FieldId,
        Required: fc.Required,
        Hidden: fc.Hidden,
        ReadOnly: fc.ReadOnly,
        Label: fc.Label,
        Description: fc.Description,
        Conditions: fc.Conditions,
        DefaultValue: fc.DefaultValue,
      }));

      // FormConfigurationParentType is filled in by Hasura from the parent
      // form_configuration row's ParentType via the fields_ordering array
      // relationship, so it must NOT be set explicitly here.
      const fieldOrderings = payload.FieldOrdering.map((fo) => ({
        FieldId: fo.FieldId,
        Position: fo.Position,
      }));

      // form_configuration object: includes the schema link if we have one,
      // and nests field ordering so it's created atomically with the row.
      const formConfigurationObject: Record<string, unknown> = {
        ParentType: parentType,
        fields_ordering: {
          data: fieldOrderings,
          on_conflict: {
            update_columns: ['Position'],
            constraint: 'form_field_configuration_pkey',
          },
        },
      };
      // Only overwrite CustomAttributeSchemaId on conflict when we have an
      // authoritative link id. A save with no schema delta (reorder, rename,
      // toggle required) carries no link id; leaving the column out of
      // update_columns preserves an existing link instead of nulling it —
      // matching the data-layer repository's preserve-on-null behaviour.
      const formConfigurationUpdateColumns: Form_Configuration_Update_Column[] =
        [Form_Configuration_Update_Column.ParentType];
      if (payload.CustomAttributeSchemaId) {
        formConfigurationObject.CustomAttributeSchemaId =
          payload.CustomAttributeSchemaId;
        formConfigurationUpdateColumns.push(
          Form_Configuration_Update_Column.CustomAttributeSchemaId
        );
      }

      const customAttributeSchemas = payload.CustomAttributeSchema
        ? [payload.CustomAttributeSchema]
        : [];

      const sectionConfig = payload.Sections.map((s) => ({
        FormConfigurationParentType: parentType,
        SectionId: s.SectionId,
        Label: s.Label,
        Description: s.Description,
        Conditions: s.Conditions,
      }));

      const sectionOrdering = payload.Sections.map((s) => ({
        FormConfigurationParentType: parentType,
        SectionId: s.SectionId,
        Position: s.Position,
      }));

      const fieldMappings = payload.Sections.flatMap((s) =>
        s.FieldIds.map((fieldId) => ({
          FormConfigurationParentType: parentType,
          SectionId: s.SectionId,
          FieldId: fieldId,
        }))
      );

      const allFieldIds = payload.FieldOrdering.map((fo) => fo.FieldId);
      const sectionIds = payload.Sections.map((s) => s.SectionId);

      await updateFormConfiguration({
        variables: {
          parentType,
          formConfigurationObject,
          formConfigurationUpdateColumns,
          fieldConfigurations,
          sectionConfig,
          sectionOrdering,
          fieldMappings,
          customAttributeSchemas,
          fieldIds: allFieldIds,
          sectionIds,
          customFieldsToDelete: payload.CustomFieldsToDelete,
        },
      });
    },
    [
      initialData,
      parentType,
      customAttributeSchema,
      formFieldConfigurations,
      updateFormConfiguration,
      standardFieldCanonicalById,
    ]
  );
};
